import { map } from "ramda";
import {
  getRequest,
  postRequest,
  checkStatus,
  parseStatusAndBody,
} from "helpers/requests";
import {
  parseScheduleSchema,
  ITransformedResponse,
  encodeSchedule,
} from "helpers/transforms/schedule";
import { HQ_WORKFORCE_SERVICE, mockPath } from "constants/app";
import { IRoleResponse } from "interfaces/pos_users";
import { ISchedule } from "types/schedules";

const MOCK_SCHEDULE = `/schedule`;
const MOCK_PUBLISH = `/publish`;

interface IAssigneeProfileResponse {
  uuid: string;
  email?: string;
  phone?: string;
  lastName: string;
  firstName: string;
  roles: IRoleResponse[];
}

interface IShiftRoleResponse {
  uuid: string;
  name: string;
}

interface IShiftResponse {
  role: IShiftRoleResponse;
  startTimeHour: number; // between 0 and 23
  startTimeMinute: number; // between 0 and 59
  endTimeHour: number; // between 0 and 23
  endTimeMinute: number; // between 0 and 59
  uuid: string;
}

interface IShiftSet {
  assignee?: IAssigneeProfileResponse;
  shifts: IShiftResponse[];
}

interface IScheduleResponse {
  updatedAt: string;
  date: string;
  people: IShiftSet[];
}

interface INotifyUserResponse {
  [uuid: string]: string;
}

interface IScheduleNotifyResponse {
  sms: INotifyUserResponse[];
}

interface INotifyBody {
  sms: INotifySmsUserBody[];
  email: INotifyEmailUserBody[];
}

interface INotifyEmailUserBody {
  email: string;
  uuid: string;
}

interface INotifySmsUserBody {
  phone: string;
  uuid: string;
}

type ISchedulesResponse = IScheduleResponse[];

// https://workforce.upserve.com/cat-s-cantina/schedules/saved?start_date=2011-02-04&end_date=2011-02-10
function getSavedSchedule(
  startDay: string,
  endDay: string,
  location: string
): Promise<ITransformedResponse[]> {
  const query = `?start_date=${encodeURIComponent(
    startDay
  )}&end_date=${encodeURIComponent(endDay)}`;

  const schedulePath = mockPath(
    `${HQ_WORKFORCE_SERVICE}/${location}/schedules/saved`,
    MOCK_SCHEDULE
  );

  return fetch(`${schedulePath}${query}`, getRequest())
    .then((response) => parseStatusAndBody<ISchedulesResponse>(response))
    .then((parsedResponse: [number, ISchedulesResponse]) =>
      checkStatus<ISchedulesResponse>(parsedResponse)
    )
    .then((resp) => map(parseScheduleSchema, resp));
}

// https://hq-workforce-api.upserve.com/cat-s-cantina/schedules/published?start_date=2011-02-04&end_date=2011-02-10
function getPublishedSchedule(
  startDay: string,
  endDay: string,
  location: string
): Promise<ITransformedResponse[]> {
  const query = `?start_date=${encodeURIComponent(
    startDay
  )}&end_date=${encodeURIComponent(endDay)}`;

  const schedulePath = mockPath(
    `${HQ_WORKFORCE_SERVICE}/${location}/schedules/published`,
    MOCK_SCHEDULE
  );

  return fetch(`${schedulePath}${query}`, getRequest())
    .then((response) => parseStatusAndBody<ISchedulesResponse>(response))
    .then((parsedResponse: [number, ISchedulesResponse]) =>
      checkStatus<ISchedulesResponse>(parsedResponse)
    )
    .then((resp) => map(parseScheduleSchema, resp));
}

function postPublishSchedule(
  location: string,
  schedule: ISchedule
): Promise<ITransformedResponse[]> {
  // https://workforce.upserve.com/cat-s-cantina/schedules/publish
  const publishPath = mockPath(
    `${HQ_WORKFORCE_SERVICE}/${location}/schedules/publish`,
    MOCK_PUBLISH
  );
  const transformed = encodeSchedule(schedule);
  return fetch(publishPath, postRequest(transformed))
    .then((response) => parseStatusAndBody<IScheduleResponse[]>(response))
    .then((parsedResponse: [number, IScheduleResponse[]]) =>
      checkStatus<IScheduleResponse[]>(parsedResponse)
    )
    .then((resp) => map(parseScheduleSchema, resp));
}

function postDraftSchedule(
  location: string,
  schedule: ISchedule
): Promise<ITransformedResponse[]> {
  const savePath = mockPath(
    `${HQ_WORKFORCE_SERVICE}/${location}/schedules/save`,
    MOCK_PUBLISH
  );
  const transformed = encodeSchedule(schedule);
  return fetch(savePath, postRequest(transformed))
    .then((response) => parseStatusAndBody<IScheduleResponse[]>(response))
    .then((parsedResponse: [number, IScheduleResponse[]]) =>
      checkStatus<IScheduleResponse[]>(parsedResponse)
    )
    .then((resp) => map(parseScheduleSchema, resp));
}

function postNotifyPublishedSchedule(
  location: string,
  startDate: string,
  endDate: string,
  notify: INotifyBody
): Promise<IScheduleNotifyResponse> {
  return fetch(
    `${HQ_WORKFORCE_SERVICE}/${location}/schedules/notify`,
    postRequest({ startDate, endDate, notify })
  )
    .then((response) => parseStatusAndBody<IScheduleNotifyResponse>(response))
    .then((parsedResponse: [number, IScheduleNotifyResponse]) =>
      checkStatus<IScheduleNotifyResponse>(parsedResponse)
    );
}

export {
  getPublishedSchedule,
  getSavedSchedule,
  postDraftSchedule,
  postPublishSchedule,
  postNotifyPublishedSchedule,
  IAssigneeProfileResponse,
  IScheduleResponse,
  ISchedulesResponse,
  IScheduleNotifyResponse,
  IShiftResponse,
  IShiftSet,
  INotifyBody,
  INotifySmsUserBody,
  INotifyEmailUserBody,
};
